import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Badge, Form } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { setOnlineOrdersPaused, setMinDeliveryTime, setShowMinDeliveryTime } from '../../store/slices/uiSlice';
import { selectActiveCallCount, selectPendingOnlineOrderCount } from '../../store/selectors';
import { isAdmin } from '../../utils/auth';
import { toggleOnlineOrders, updateMinDeliveryTime } from '../../services/api';
import PrinterIndicators from './PrinterIndicators';
import { NAV_ITEM, type NavKey } from '../../config/navItems';

export default function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = useAppSelector((s) => s.auth.userName);
  const isConnected = useAppSelector((s) => s.auth.isConnected);
  const activeAgent = useAppSelector((s) => s.ui.activeAgent);
  const activeCallCount = useAppSelector(selectActiveCallCount);
  const pendingOnlineOrderCount = useAppSelector(selectPendingOnlineOrderCount);
  const onlineOrdersPaused = useAppSelector((s) => s.ui.onlineOrdersPaused);
  const minDeliveryTime = useAppSelector((s) => s.ui.minDeliveryTime);
  const showMinDeliveryTime = useAppSelector((s) => s.ui.showMinDeliveryTime);

  const [toggling, setToggling] = useState(false);
  const [localMinTime, setLocalMinTime] = useState<string>('');
  const minTimeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync Redux → local input
  useState(() => { setLocalMinTime(minDeliveryTime != null ? String(minDeliveryTime) : ''); });
  useEffect(() => { setLocalMinTime(minDeliveryTime != null ? String(minDeliveryTime) : ''); }, [minDeliveryTime]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleMinTimeChange = (val: string) => {
    setLocalMinTime(val);
    if (minTimeRef.current) clearTimeout(minTimeRef.current);
    minTimeRef.current = setTimeout(async () => {
      let minutes = val.trim() ? parseInt(val, 10) : null;
      if (minutes !== null && isNaN(minutes)) return;
      // Clamp to [0, 90] — typed-in values bypass the input's max attr.
      if (minutes !== null) minutes = Math.max(0, Math.min(90, minutes));
      try {
        await updateMinDeliveryTime({ minutes });
        dispatch(setMinDeliveryTime(minutes));
        if (minutes !== null) setLocalMinTime(String(minutes));
      } catch { /* ignore */ }
    }, 800);
  };

  const handleToggleShowMinTime = async () => {
    const newVal = !showMinDeliveryTime;
    dispatch(setShowMinDeliveryTime(newVal));
    try {
      await updateMinDeliveryTime({ show: newVal });
    } catch { /* ignore */ }
  };

  const handleToggleOnlineOrders = async () => {
    setToggling(true);
    try {
      const newPaused = !onlineOrdersPaused;
      await toggleOnlineOrders(newPaused);
      dispatch(setOnlineOrdersPaused(newPaused));
    } catch {
      // ignore
    }
    setToggling(false);
  };

  const reservationsEnabled = useAppSelector((s) => s.ui.reservationsEnabled);
  const ivrNewUi = useAppSelector((s) => s.ui.ivrNewUi);
  const pendingReservationCount = useAppSelector((s) => s.ui.pendingReservationCount);
  const view = useAppSelector((s) => s.ui.uiVisibleComponents);
  const tabletView = view.ui_tablet_view;
  const admin = isAdmin();

  // Business-wide "Postavke → Navigacija": entries hidden from every non-admin.
  const navHidden = useAppSelector((s) => s.ui.navHidden);
  // Both visibility sources arrive after the socket connects. Until they have,
  // a non-admin sees none of the hideable entries: the per-user flags start out
  // `true` and navHidden starts empty, so deciding earlier would flash entries
  // the admin hid. Fail closed: if a fetch fails they stay hidden until the
  // next (re)connect refetches them.
  const uiSettingsLoaded = useAppSelector((s) => s.ui.uiSettingsLoaded);
  const navHiddenLoaded = useAppSelector((s) => s.ui.navHiddenLoaded);
  const navReady = uiSettingsLoaded && navHiddenLoaded;

  // Admins always see everything. A non-admin sees an entry only once both
  // sources are loaded, if it is allowed for them (per-user ui_view_* flag /
  // feature gate, `allowed`) AND the business hasn't hidden it.
  const show = (key: NavKey, allowed: boolean) =>
    admin || (navReady && allowed && !navHidden.includes(key));
  const entry = (key: NavKey) => {
    const { path, label, icon } = NAV_ITEM[key];
    return { path, label, icon };
  };

  const navItems = [
    // 'Telefon' was a duplicate entry point — the HalloChef brand already
    // navigates to /order, so removing it here cleans up the nav bar.
    ...(show('deliveries', !!view.ui_view_deliveries) ? [{ ...entry('deliveries'), badge: pendingOnlineOrderCount > 0 ? pendingOnlineOrderCount : null, badgeVariant: 'warning' as const }] : []),
    ...(show('calls', !!view.ui_view_calls) ? [{ ...entry('calls'), badge: activeCallCount > 0 ? activeCallCount : null, badgeVariant: 'danger' as const }] : []),
    // Reservations is a business feature switch: off hides it for admins too.
    ...(reservationsEnabled && show('reservations', true) ? [{ ...entry('reservations'), badge: pendingReservationCount > 0 ? pendingReservationCount : null, badgeVariant: 'warning' as const }] : []),
    ...(show('handoff', !!view.ui_view_handoff) ? [entry('handoff')] : []),
    ...(show('menu', !!view.ui_view_menu) ? [entry('menu')] : []),
    ...(show('tables', !!tabletView) ? [entry('tables')] : []),
    ...(show('local_orders', !!tabletView) ? [entry('local_orders')] : []),
    ...(show('deliverymen', !!view.ui_view_deliverymen) ? [entry('deliverymen')] : []),
    ...(isAdmin() ? [
      { path: '/stats', label: 'Stats', icon: '📊' },
      { path: '/trends', label: 'Trends', icon: '📈' },
      // One entry, two screens: cfg_ivr_new_ui on the business decides whether
      // it opens the prompt-and-schedule builder or the original mode-code
      // screen. Both routes stay registered, so a business that has been
      // switched over can still be sent a direct link to the old one.
      ivrNewUi
        ? { path: '/ivr-new', label: 'Raspored', icon: '🗓️' }
        : { path: '/ivr', label: 'IVR', icon: '🎙️' },
      { path: '/settings', label: 'Postavke', icon: '⚙️' },
    ] : []),
  ];

  const displayName = activeAgent ? `${userName} (${activeAgent})` : userName;

  const onlineActive = !onlineOrdersPaused;

  return (
    <>
    <Navbar bg="dark" variant="dark" expand="md" sticky="top">
      <Container fluid>
        {/* Brand goes to the user's default view: tablet (waiter) users land on
            the floor plan, everyone else on the phone-order screen. */}
        <Navbar.Brand onClick={() => navigate(tabletView ? '/tables' : '/order')} style={{ cursor: 'pointer' }}>
          HalloChef
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="main-navbar" />
        <Navbar.Collapse id="main-navbar">
          <Nav className="me-auto">
            {navItems.map((item) => (
              <Nav.Link
                key={item.path}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                title={item.label}
              >
                {item.icon ? (
                  <>
                    <span style={{ fontSize: '1.1rem', marginRight: 4 }}>{item.icon}</span>
                    {/* Icons-only on the inline bar (md–lg, incl. tablet portrait/landscape) where
                        horizontal room is tight; phones (collapsed menu) and xl+ show labels. */}
                    <span className="d-inline d-md-none d-xl-inline">{item.label}</span>
                  </>
                ) : (
                  item.label
                )}
                {'badge' in item && item.badge != null && (
                  <>
                    {' '}
                    <Badge bg={item.badgeVariant} pill>{item.badge}</Badge>
                  </>
                )}
              </Nav.Link>
            ))}
          </Nav>
          <Nav className="align-items-md-center flex-wrap gap-2 gap-md-0">
            <div className="d-flex align-items-center flex-wrap gap-2 me-md-3 py-2 py-md-0">
              {(admin || (uiSettingsLoaded && view.ui_view_min_delivery_time)) && (
              <div className="hc-nav-pill" title="Min. vrijeme dostave (min) — prikazano kupcima">
                <button
                  type="button"
                  onClick={handleToggleShowMinTime}
                  title={showMinDeliveryTime ? 'Sakrij od kupaca' : 'Prikaži kupcima'}
                  className="hc-nav-pill__icon-btn"
                  data-off={!showMinDeliveryTime}
                >
                  {showMinDeliveryTime ? '👁' : '👁‍🗨'}
                </button>
                <input
                  type="number"
                  min={0}
                  max={90}
                  step={5}
                  value={localMinTime}
                  onChange={(e) => handleMinTimeChange(e.target.value)}
                  placeholder="min"
                  title="Min. vrijeme dostave (min)"
                />
                <span style={{ opacity: 0.7 }}>min</span>
              </div>
              )}
              {(admin || (uiSettingsLoaded && view.ui_view_online_toggle)) && (
              <div className={`hc-nav-pill ${onlineActive ? 'hc-nav-pill--success' : 'hc-nav-pill--danger'}`}>
                <span>Online narudžbe</span>
                <Form.Check
                  type="switch"
                  id="online-orders-toggle"
                  checked={onlineActive}
                  onChange={handleToggleOnlineOrders}
                  disabled={toggling}
                  className="mb-0"
                />
              </div>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2 me-md-3 py-1 py-md-0">
              <PrinterIndicators />
            </div>
            <Navbar.Text className="me-md-3">
              {isConnected ? '🟢' : '🔴'} {displayName}
            </Navbar.Text>
            <div className="d-flex gap-2 py-2 py-md-0">
              <Button variant="outline-light" size="sm" onClick={handleLogout}>
                Odjava
              </Button>
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    </>
  );
}
